import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useTranslation } from "react-i18next";
import { useAppStore } from "lib/store/useAppStore";
import { getFusionResultLabel } from "lib/utils/fusionResult";

const SHEET_COLUMN_WIDTH = 9;

const SHEET_ROW_HEIGHTS = {
  1: 33.95,
  2: 14.1,
  3: 33,
  4: 33,
  5: 33,
  6: 90,
  7: 27.75,
  8: 70,
  9: 70,
  10: 27.95,
  11: 162,
  12: 21.95,
  13: 21.95,
  14: 21.95,
  15: 21.95,
  16: 21.95,
};

// STYLE: 상단 요약 라벨 셀(A3:B3, E3:F3, I3:J3 / A4:B4, E4:F4, I4:J4 / A5:B5, E5:F5, I5:J5)
// - 용도: 설비구분, 설비명, 부품명, 분석 기간, 이미지 종류, 분석 데이터 수, 평균값 라벨 배경
// - 배경색: FFF3F4F6 (연한 회색)
// - 같이 쓰는 폰트: SUMMARY_LABEL_FONT (Malgun Gothic 9pt, #111827)
// - 의도: 값 셀(C:D, G:H, K:L)보다 라벨 영역을 은은하게 구분
const SUMMARY_LABEL_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFF3F4F6" },
};

// STYLE: 대표 이미지 상세 라벨 셀(12~16행의 라벨 영역)
// - 3개 구성: A:B, E:F, I:J
// - 2개 구성: A:B, G:H
// - 용도: 파일명, 초음파, 검출 온도, 3상 비교 온도차, 점검결과 라벨 배경
// - 배경색: FFF3F4F6 (연한 회색)
// - 같이 쓰는 폰트: BODY_TEXT_FONT (Malgun Gothic 8pt, #111827)
// - 의도: 값 셀은 흰색으로 두고 라벨 열만 강조해서 표 형태를 분명하게 보이게 함
const DETAIL_LABEL_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFF3F4F6" },
};

// STYLE: 섹션 제목 셀(A7:F7, G7:L7)
// - 용도: 초음파 추이 그래프 / 온도 추이 그래프 제목 행 배경
// - 배경색: FFEFF6FF (연한 하늘색)
// - 같이 쓰는 폰트: SECTION_HEADER_FONT (Malgun Gothic 9pt, #1E3A8A)
// - 의도: 상단 요약 구역과 차트 구역의 시작을 시각적으로 분리
const SECTION_HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFEFF6FF" },
};

// STYLE: 대표 이미지 날짜 셀(10행의 날짜 영역)
// - 3개 구성: A10:D10, E10:H10, I10:L10
// - 2개 구성: A10:F10, G10:L10
// - 용도: 각 대표 이미지 촬영/분석 시점 표시 행 배경
// - 배경색: FFEFF6FF (연한 하늘색)
// - 같이 쓰는 폰트: DATE_TEXT_FONT (Malgun Gothic 8pt, #1E3A8A)
// - 의도: 아래 이미지/상세 정보 블록의 시작점을 날짜 행으로 먼저 구분
const REPRESENTATIVE_DATE_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFEFF6FF" },
};

// STYLE: 이미지 영역 셀(대표 이미지 삽입 영역 11행 전체)
// - 3개 구성: A11:D11, E11:H11, I11:L11
// - 2개 구성: A11:F11, G11:L11
// - 용도: 실제 대표 이미지가 들어가는 자리의 기본 배경색
// - 배경색: FFF8FAFC (아주 옅은 회백색)
// - 같이 쓰는 폰트: SUBTLE_TEXT_FONT (Malgun Gothic 8pt, #64748B)
// - 의도: 이미지가 없는 경우 "No Image" 같은 안내 문구가 튀지 않도록 부드러운 배경 사용
const IMAGE_AREA_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFF8FAFC" },
};

// STYLE: 값 셀 기본 배경
// - 용도:
//   1) 종합의견 값 영역 C6:L6
//   2) 차트 이미지 삽입 영역 A8:F9, G8:L9
//   3) 대표 이미지 상세 값 셀(예: 12C:D, 12G:H, 12K:L / 2개일 때 12C:F, 12I:L)
// - 배경색: FFFFFFFF (흰색)
// - 같이 쓰는 폰트: SUMMARY_VALUE_FONT 또는 BODY_TEXT_FONT
// - 의도: 라벨 셀의 회색/하늘색 대비를 주고 실제 값이 입력되는 칸은 비워 보이게 처리
const WHITE_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFFFFFF" },
};

// STYLE: 보고서 전체 표 기본 테두리
// - 적용 구간:
//   1) 상단 요약 표 3~7행
//   2) 대표 이미지 상세 비교 표 10~16행
// - 테두리 스타일: thin
// - 테두리 색상: FFD1D5DB (연한 회색)
// - 의도: 강한 검정 테두리 대신 부드러운 회색 선으로 표 구조만 또렷하게 유지
const THIN_BORDER = {
  top: { style: "thin", color: { argb: "FFD1D5DB" } },
  left: { style: "thin", color: { argb: "FFD1D5DB" } },
  bottom: { style: "thin", color: { argb: "FFD1D5DB" } },
  right: { style: "thin", color: { argb: "FFD1D5DB" } },
};

// STYLE: 상단 요약 라벨 폰트
// - 적용 셀: 3~5행 라벨 셀(A:B / E:F / I:J)
// - 글꼴: Malgun Gothic
// - 글자 크기: 9pt
// - 글자 색상: FF111827 (짙은 남회색)
// - 의도: 라벨 텍스트를 값보다 약간 또렷하게 보이도록 기본 요약용 폰트 사용
const SUMMARY_LABEL_FONT = {
  name: "Malgun Gothic",
  size: 9,
  bold: true,
  color: { argb: "FF111827" },
};

// STYLE: 상단 요약 값 폰트
// - 적용 셀: 3~5행 값 셀(C:D / G:H / K:L)
// - 글꼴: Malgun Gothic
// - 글자 크기: 9pt
// - 글자 색상: FF111827 (짙은 남회색)
// - 의도: 라벨과 동일한 크기로 맞추되 배경 차이로 정보 계층을 구분
const SUMMARY_VALUE_FONT = {
  name: "Malgun Gothic",
  size: 9,
  color: { argb: "FF111827" },
};

// STYLE: 본문 상세 텍스트 폰트
// - 적용 셀:
//   1) 종합의견 값 영역 C6:L6
//   2) 대표 이미지 상세 표 12~16행 라벨/값 셀
// - 글꼴: Malgun Gothic
// - 글자 크기: 8pt
// - 글자 색상: FF111827 (짙은 남회색)
// - 의도: 본문 정보량이 많아지는 구간이라 8pt로 밀도를 확보
const BODY_TEXT_FONT = {
  name: "Malgun Gothic",
  size: 8,
  color: { argb: "FF111827" },
};

const DETAIL_LABEL_FONT = {
  name: "Malgun Gothic",
  size: 8,
  bold: true,
  color: { argb: "FF111827" },
};

// STYLE: 차트 섹션 제목 폰트
// - 적용 셀: A7:F7, G7:L7
// - 글꼴: Malgun Gothic
// - 글자 크기: 9pt
// - 글자 색상: FF1E3A8A (짙은 파란색)
// - 의도: 차트 제목을 본문과 구분되게 보이도록 파란 계열 강조색 사용
const SECTION_HEADER_FONT = {
  name: "Malgun Gothic",
  size: 9,
  bold: true,
  color: { argb: "FF1E3A8A" },
};

// STYLE: 이미지 영역 안내용 보조 폰트
// - 적용 셀: 대표 이미지 영역 11행에서 이미지가 없을 때 표시되는 안내 문구
// - 글꼴: Malgun Gothic
// - 글자 크기: 8pt
// - 글자 색상: FF64748B (회청색)
// - 의도: "No Image" 같은 보조 문구가 본문 데이터보다 덜 눈에 띄게 처리
const SUBTLE_TEXT_FONT = {
  name: "Malgun Gothic",
  size: 8,
  color: { argb: "FF64748B" },
};

// STYLE: 대표 이미지 날짜 행 폰트
// - 적용 셀: 10행 날짜 셀(A10:D10, E10:H10, I10:L10 또는 A10:F10, G10:L10)
// - 글꼴: Malgun Gothic
// - 글자 크기: 8pt
// - 글자 색상: FF1E3A8A (짙은 파란색)
// - 의도: 날짜 행을 상세 값과 구분하면서도 차트 제목보다 과하지 않게 표시
const DATE_TEXT_FONT = {
  name: "Malgun Gothic",
  size: 8,
  bold: true,
  color: { argb: "FF1E3A8A" },
};

const REPORT_TITLE_FONT = {
  name: "Malgun Gothic",
  size: 14,
  bold: true,
  color: { argb: "FF111827" },
};

function getDisplayText(value, fallback = "-") {
  const text = String(value ?? "").trim();
  return text || fallback;
}

function toNumeric(value) {
  const parsed = Number(String(value ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

function formatMetricValue(value, digits = 1) {
  const numeric = toNumeric(value);
  return numeric == null ? "-" : numeric.toFixed(digits);
}

function formatThreePhaseDifferenceValue(clickPoints, digits = 1) {
  const values = Array.isArray(clickPoints)
    ? clickPoints
        .map((point) => toNumeric(point?.value))
        .filter((value) => value != null)
    : [];

  if (values.length === 0) {
    return "-";
  }

  return (Math.max(...values) - Math.min(...values)).toFixed(digits);
}

function normalizeOpinionText(value) {
  return String(value ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/\n{2,}/g, "\n")
    .trim();
}

function setCellValue(sheet, address, value, alignment = {}) {
  const cell = sheet.getCell(address);
  cell.value = value;
  cell.alignment = {
    ...(cell.alignment ?? {}),
    vertical: "middle",
    wrapText: true,
    ...alignment,
  };
}

function addBase64Image(workbook, sheet, base64, range) {
  if (!base64 || typeof base64 !== "string") return;

  const imageId = workbook.addImage({
    base64,
    extension: "png",
  });

  sheet.addImage(imageId, range);
}

function applyBorder(cell) {
  cell.border = THIN_BORDER;
}

function applyBorderRange(sheet, startRow, endRow, startCol, endCol) {
  for (let row = startRow; row <= endRow; row += 1) {
    for (let col = startCol; col <= endCol; col += 1) {
      applyBorder(sheet.getRow(row).getCell(col));
    }
  }
}

function applyRangeFill(sheet, rowNumber, startCol, endCol, fill) {
  for (let col = startCol; col <= endCol; col += 1) {
    sheet.getRow(rowNumber).getCell(col).fill = fill;
  }
}

function columnNumberToName(columnNumber) {
  let column = columnNumber;
  let name = "";

  while (column > 0) {
    const remainder = (column - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    column = Math.floor((column - 1) / 26);
  }

  return name;
}

function toRange(startCol, startRow, endCol, endRow = startRow) {
  return `${columnNumberToName(startCol)}${startRow}:${columnNumberToName(endCol)}${endRow}`;
}

function getRangeStartAddress(range) {
  return range.split(":")[0];
}

function mergeRange(sheet, range) {
  try {
    sheet.mergeCells(range);
  } catch (error) {
    // Ignore ranges already merged in the expected shape.
  }
}

function applyRangeFont(sheet, rowNumber, startCol, endCol, font) {
  for (let col = startCol; col <= endCol; col += 1) {
    sheet.getRow(rowNumber).getCell(col).font = font;
  }
}

function applyRangeAlignment(sheet, rowNumber, startCol, endCol, alignment) {
  for (let col = startCol; col <= endCol; col += 1) {
    const cell = sheet.getRow(rowNumber).getCell(col);
    cell.alignment = {
      ...(cell.alignment ?? {}),
      vertical: "middle",
      wrapText: true,
      ...alignment,
    };
  }
}

function getChartBase64(chartRef) {
  try {
    const chart = chartRef?.current;
    const sourceCanvas = chart?.canvas;

    if (!sourceCanvas) {
      return chart?.toBase64Image?.() || "";
    }

    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = sourceCanvas.width;
    exportCanvas.height = sourceCanvas.height;

    const context = exportCanvas.getContext("2d");
    if (!context) {
      return chart?.toBase64Image?.() || "";
    }

    context.fillStyle = "#FFFFFF";
    context.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    context.drawImage(sourceCanvas, 0, 0);

    return exportCanvas.toDataURL("image/png");
  } catch (error) {
    console.warn("[TimeseriesExcelDownloadBtn] chart capture failed", error);
    return "";
  }
}

function getReportImageSource(images, detailedFile, selectedImageType) {
  if (selectedImageType === "ultrasonic") {
    return images?.image_visual ?? detailedFile?.image_visual ?? "";
  }

  if (selectedImageType === "thermal") {
    return (
      images?.markup_image_triplespot ??
      images?.markup_image_thermal ??
      detailedFile?.markup_image_triplespot ??
      detailedFile?.markup_image_thermal ??
      ""
    );
  }

  return images?.image_uv ?? detailedFile?.image_uv ?? "";
}

// HELPER: 대표 이미지 개수에 따라 하단 비교 블록의 병합 범위를 나눈다.
function getRepresentativeBlockRanges(count) {
  if (count >= 3) {
    return [
      createRepresentativeBlock(1, 4),
      createRepresentativeBlock(5, 8),
      createRepresentativeBlock(9, 12),
    ];
  }

  if (count === 2) {
    return [createRepresentativeBlock(1, 6), createRepresentativeBlock(7, 12)];
  }

  return [createRepresentativeBlock(1, 12)];
}

// HELPER: 각 대표 이미지 블록의 날짜/이미지/상세 정보 셀 범위를 한 곳에서 계산한다.
function createRepresentativeBlock(startCol, endCol, labelWidth = 2) {
  const labelEndCol = Math.min(startCol + labelWidth - 1, endCol);
  const valueStartCol = Math.min(labelEndCol + 1, endCol);

  return {
    startCol,
    endCol,
    labelEndCol,
    valueStartCol,
    date: toRange(startCol, 10, endCol, 10),
    image: toRange(startCol, 11, endCol, 11),
    fields: {
      fileName: {
        label: toRange(startCol, 12, labelEndCol, 12),
        value: toRange(valueStartCol, 12, endCol, 12),
      },
      ultrasonic: {
        label: toRange(startCol, 13, labelEndCol, 13),
        value: toRange(valueStartCol, 13, endCol, 13),
      },
      detectedTemperature: {
        label: toRange(startCol, 14, labelEndCol, 14),
        value: toRange(valueStartCol, 14, endCol, 14),
      },
      threePhaseDifference: {
        label: toRange(startCol, 15, labelEndCol, 15),
        value: toRange(valueStartCol, 15, endCol, 15),
      },
      inspectionResult: {
        label: toRange(startCol, 16, labelEndCol, 16),
        value: toRange(valueStartCol, 16, endCol, 16),
      },
    },
  };
}

// HELPER: 기본 템플릿을 현재 시계열 보고서 행 구조에 맞게 재배열한다.
function applySheetDimensions(sheet) {
  for (let col = 1; col <= 12; col += 1) {
    sheet.getColumn(col).width = SHEET_COLUMN_WIDTH;
  }

  Object.entries(SHEET_ROW_HEIGHTS).forEach(([rowNumber, height]) => {
    sheet.getRow(Number(rowNumber)).height = height;
  });
}

function applyPageBreakPreview(sheet) {
  sheet.views = [
    {
      state: "normal",
      style: "pageBreakPreview",
      showGridLines: false,
      zoomScale: 100,
      zoomScaleNormal: 100,
    },
  ];
}

function initializeWorksheet(sheet, representativeBlocks, t) {
  applySheetDimensions(sheet);
  applyPageBreakPreview(sheet);

  [
    "A1:L1",
    "A2:L2",
    "A3:B3",
    "C3:D3",
    "E3:F3",
    "G3:H3",
    "I3:J3",
    "K3:L3",
    "A4:B4",
    "C4:D4",
    "E4:F4",
    "G4:H4",
    "I4:J4",
    "K4:L4",
    "A5:B5",
    "C5:D5",
    "E5:F5",
    "G5:H5",
    "I5:J5",
    "K5:L5",
    "A6:B6",
    "C6:L6",
    "A7:F7",
    "G7:L7",
    "A8:F9",
    "G8:L9",
  ].forEach((range) => mergeRange(sheet, range));

  representativeBlocks.forEach((block) => {
    mergeRange(sheet, block.date);
    mergeRange(sheet, block.image);

    Object.values(block.fields).forEach((field) => {
      mergeRange(sheet, field.label);
      mergeRange(sheet, field.value);
    });
  });

  setCellValue(
    sheet,
    "A1",
    t("imageFileIndex.reportTemplateSlide.templates.timeseries.title", {
      defaultValue: "Equipment Timeseries Diagnostic Report",
    }),
    { horizontal: "center", wrapText: false },
  );
  sheet.getCell("A1").font = REPORT_TITLE_FONT;

  setCellValue(sheet, "A2", "", {
    horizontal: "center",
    wrapText: false,
  });

  applyRangeAlignment(sheet, 1, 1, 12, { horizontal: "center" });
  applyRangeAlignment(sheet, 2, 1, 12, { horizontal: "center" });
  applyRangeFill(sheet, 1, 1, 12, SECTION_HEADER_FILL);
  applyRangeFill(sheet, 2, 1, 12, WHITE_FILL);

  applyBorderRange(sheet, 3, 7, 1, 12);
  applyBorderRange(sheet, 10, 16, 1, 12);

  [3, 4, 5].forEach((rowNumber) => {
    [1, 5, 9].forEach((startCol) => {
      applyRangeFill(
        sheet,
        rowNumber,
        startCol,
        startCol + 1,
        SUMMARY_LABEL_FILL,
      );
      applyRangeFill(sheet, rowNumber, startCol + 2, startCol + 3, WHITE_FILL);
      applyRangeFont(
        sheet,
        rowNumber,
        startCol,
        startCol + 1,
        SUMMARY_LABEL_FONT,
      );
      applyRangeFont(
        sheet,
        rowNumber,
        startCol + 2,
        startCol + 3,
        SUMMARY_VALUE_FONT,
      );
    });
  });

  applyRangeFill(sheet, 6, 1, 2, SUMMARY_LABEL_FILL);
  applyRangeFill(sheet, 6, 3, 12, WHITE_FILL);
  applyRangeFont(sheet, 6, 1, 2, SUMMARY_LABEL_FONT);
  applyRangeFont(sheet, 6, 3, 12, BODY_TEXT_FONT);

  applyRangeFill(sheet, 7, 1, 12, SECTION_HEADER_FILL);
  applyRangeFont(sheet, 7, 1, 12, SECTION_HEADER_FONT);

  [8, 9].forEach((rowNumber) => {
    applyRangeFill(sheet, rowNumber, 1, 12, WHITE_FILL);
  });

  representativeBlocks.forEach((block) => {
    applyRangeFill(
      sheet,
      10,
      block.startCol,
      block.endCol,
      REPRESENTATIVE_DATE_FILL,
    );
    applyRangeFont(sheet, 10, block.startCol, block.endCol, DATE_TEXT_FONT);

    applyRangeFill(sheet, 11, block.startCol, block.endCol, IMAGE_AREA_FILL);
    applyRangeFont(sheet, 11, block.startCol, block.endCol, SUBTLE_TEXT_FONT);

    for (let row = 12; row <= 16; row += 1) {
      applyRangeFill(
        sheet,
        row,
        block.startCol,
        block.labelEndCol,
        DETAIL_LABEL_FILL,
      );
      applyRangeFill(sheet, row, block.valueStartCol, block.endCol, WHITE_FILL);
      applyRangeFont(
        sheet,
        row,
        block.startCol,
        block.labelEndCol,
        DETAIL_LABEL_FONT,
      );
      applyRangeFont(
        sheet,
        row,
        block.valueStartCol,
        block.endCol,
        BODY_TEXT_FONT,
      );
    }

    applyRangeAlignment(sheet, 10, block.startCol, block.endCol, {
      horizontal: "center",
    });
    applyRangeAlignment(sheet, 11, block.startCol, block.endCol, {
      horizontal: "center",
    });
    applyRangeAlignment(sheet, 16, block.startCol, block.labelEndCol, {
      horizontal: "center",
    });
    applyRangeAlignment(sheet, 16, block.valueStartCol, block.endCol, {
      horizontal: "center",
    });
  });
}

// HELPER: 2개 / 3개 대표 이미지 구성에서 예시 양식과 다른 셀을 마지막에 한 번 더 보정한다.
function applyRepresentativeBlockOverrides(sheet, representativeBlocks) {
  if (representativeBlocks.length === 2) {
    applyRangeFill(sheet, 12, 3, 6, WHITE_FILL);
    applyRangeFill(sheet, 12, 9, 12, WHITE_FILL);
    applyRangeFill(sheet, 15, 1, 2, DETAIL_LABEL_FILL);
    applyRangeFill(sheet, 16, 1, 2, DETAIL_LABEL_FILL);
  }
}

// HELPER: 예시 양식 기준의 병합, 배경색, 폰트, 정렬을 한 번에 다시 적용한다.
function configureSheetLayout(sheet, representativeBlocks, t) {
  initializeWorksheet(sheet, representativeBlocks, t);
  applyRepresentativeBlockOverrides(sheet, representativeBlocks);

  setCellValue(
    sheet,
    "A3",
    t("imageFileIndex.timeseries.summary.facilityType", {
      defaultValue: "Facility Type",
    }),
    { horizontal: "center" },
  );
  setCellValue(
    sheet,
    "E3",
    t("imageFileIndex.timeseries.summary.facilityName", {
      defaultValue: "Facility Name",
    }),
    { horizontal: "center" },
  );
  setCellValue(
    sheet,
    "I3",
    t("imageFileIndex.timeseries.summary.partName", {
      defaultValue: "Part Name",
    }),
    { horizontal: "center" },
  );

  setCellValue(
    sheet,
    "A4",
    t("imageFileIndex.timeseries.summary.analysisPeriod", {
      defaultValue: "Analysis Period",
    }),
    { horizontal: "center" },
  );
  setCellValue(
    sheet,
    "E4",
    t("imageFileIndex.timeseries.config.imageType", {
      defaultValue: "Image Type",
    }),
    {
      horizontal: "center",
    },
  );
  setCellValue(
    sheet,
    "I4",
    t("imageFileIndex.timeseries.kpi.dataCount", {
      defaultValue: "Analysis Data Count",
    }),
    {
      horizontal: "center",
    },
  );

  setCellValue(
    sheet,
    "A5",
    `${t("imageFileIndex.timeseries.kpi.averageUltrasonic", {
      defaultValue: "Average Ultrasonic",
    })}\n(dB)`,
    { horizontal: "center" },
  );
  setCellValue(
    sheet,
    "E5",
    `${t("imageFileIndex.timeseries.chart.detectedTemperature", {
      defaultValue: "Detected Temperature",
    })}\n(\u00B0C)`,
    { horizontal: "center" },
  );
  setCellValue(
    sheet,
    "I5",
    `${t("imageFileIndex.timeseries.chart.threePhaseDifference", {
      defaultValue: "Three-phase Temperature Difference",
    })}\n(\u00B0C)`,
    { horizontal: "center" },
  );

  setCellValue(
    sheet,
    "A6",
    t("imageFileIndex.timeseries.summary.comprehensiveOpinion", {
      defaultValue: "Comprehensive Opinion",
    }),
    { horizontal: "center" },
  );

  representativeBlocks.forEach((block) => {
    setCellValue(
      sheet,
      getRangeStartAddress(block.fields.fileName.label),
      t("imageFileIndex.timeseries.detail.fileName", {
        defaultValue: "File Name",
      }),
      { horizontal: "center" },
    );
    setCellValue(
      sheet,
      getRangeStartAddress(block.fields.ultrasonic.label),
      t("imageFileIndex.timeseries.detail.ultrasonic", {
        defaultValue: "Ultrasonic",
      }),
      { horizontal: "center" },
    );
    setCellValue(
      sheet,
      getRangeStartAddress(block.fields.detectedTemperature.label),
      t("imageFileIndex.timeseries.detail.detectedTemperature", {
        defaultValue: "Detected Temperature",
      }),
      { horizontal: "center" },
    );
    setCellValue(
      sheet,
      getRangeStartAddress(block.fields.threePhaseDifference.label),
      t("imageFileIndex.timeseries.detail.threePhaseDifference", {
        defaultValue: "Three-phase Difference",
      }),
      { horizontal: "center" },
    );
    setCellValue(
      sheet,
      getRangeStartAddress(block.fields.inspectionResult.label),
      t("imageFileIndex.timeseries.detail.inspectionResult", {
        defaultValue: "Inspection Result",
      }),
      { horizontal: "center" },
    );
  });
}

/*
 * TimeseriesExcelDownloadBtn 역할:
 * - 시계열 보고서 템플릿을 불러와 예시 양식과 동일한 시트 구조로 다시 구성한다.
 * - 요약 정보, 차트 이미지, 대표 이미지 상세 비교 정보를 엑셀에 채워 내려받는다.
 * - 대표 이미지는 2~3개 선택된 경우에만 내려받기를 허용한다.
 */
export default function TimeseriesExcelDownloadBtn({
  file,
  selectedFiles,
  selectedImageTypeLabel,
  fileImages,
  imageType,
  representativeUploadIdSet,
  ultrasonicChartRef,
  temperatureChartRef,
  temperatureChartMode,
  averageUltrasonic,
  averageTemperature,
  averageThreePhaseDiffValue,
  comprehensiveOpinion,
  ultrasonicOpinion,
  temperatureOpinion,
}) {
  const { i18n, t } = useTranslation();
  const showAlert = useAppStore((s) => s.showAlert);
  const representativeCount = representativeUploadIdSet?.size ?? 0;
  const canDownload = representativeCount >= 2 && representativeCount <= 3;
  const representativeGuideMessage = t(
    "imageFileIndex.timeseries.messages.selectTwoOrThreeRepresentatives",
    {
      defaultValue:
        "Excel 보고서를 다운로드하려면 대표 이미지를 2~3개 선택하세요.",
    },
  );
  const downloadFailedMessage = t(
    "imageFileIndex.timeseries.messages.excelDownloadFailed",
    {
      defaultValue: "시계열 Excel 보고서를 만들지 못했습니다. 다시 시도해 주세요.",
    },
  );

  const handleDownload = async () => {
    if (!canDownload) {
      showAlert({
        text: representativeGuideMessage,
        success: false,
        autoClose: true,
      });
      return;
    }

    try {
      // STEP 1: 템플릿을 불러오고 기본 페이지/시트 구조를 예시 양식에 맞춘다.
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("TimeseriesReport");

      sheet.pageSetup = {
        ...sheet.pageSetup,
        paperSize: 9,
        orientation: "landscape",
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 1,
        horizontalCentered: true,
        margins: {
          left: 0.2,
          right: 0.2,
          top: 0.25,
          bottom: 0.25,
          header: 0.1,
          footer: 0.1,
        },
      };

      const sortedFiles = [...(selectedFiles ?? [])];
      const periodStart = sortedFiles[0];
      const periodEnd = sortedFiles[sortedFiles.length - 1];
      const representativeFiles = sortedFiles
        .filter((item) =>
          representativeUploadIdSet?.has(String(item?.upload_id ?? "")),
        )
        .slice(0, 3);

      if (representativeFiles.length < 2) {
        showAlert({
          text: representativeGuideMessage,
          success: false,
          autoClose: true,
        });
        return;
      }

      // STEP 2: 대표 이미지 개수에 맞는 블록 레이아웃과 요약 텍스트를 계산한다.
      const visibleRepresentativeFiles =
        representativeFiles.length > 0
          ? representativeFiles
          : sortedFiles.slice(0, 1);
      const representativeBlocks = getRepresentativeBlockRanges(
        visibleRepresentativeFiles.length,
      );
      const temperatureGraphTitle =
        temperatureChartMode === "threePhaseDiff"
          ? `${t("imageFileIndex.timeseries.chart.temperatureTrend", {
              defaultValue: "Temperature Trend",
            })} (${t("imageFileIndex.timeseries.chart.threePhaseDifference", {
              defaultValue: "Three-phase Difference",
            })})`
          : `${t("imageFileIndex.timeseries.chart.temperatureTrend", {
              defaultValue: "Temperature Trend",
            })} (${t("imageFileIndex.timeseries.chart.detectedTemperature", {
              defaultValue: "Detected Temperature",
            })})`;
      const combinedOpinion = normalizeOpinionText(
        String(comprehensiveOpinion ?? "").trim() ||
          [ultrasonicOpinion, temperatureOpinion].filter(Boolean).join(" "),
      );

      // STEP 3: 상단 요약 정보와 차트 영역을 채운다.
      configureSheetLayout(sheet, representativeBlocks, t);

      setCellValue(sheet, "C3", getDisplayText(file?.facility_type), {
        horizontal: "center",
      });
      setCellValue(sheet, "G3", getDisplayText(file?.facility_name), {
        horizontal: "center",
      });
      setCellValue(sheet, "K3", getDisplayText(file?.part_name), {
        horizontal: "center",
      });

      setCellValue(
        sheet,
        "C4",
        `${getDisplayText(periodStart?.shooting_date)} ~ ${getDisplayText(periodEnd?.shooting_date)}`,
        { horizontal: "center" },
      );
      setCellValue(sheet, "G4", getDisplayText(selectedImageTypeLabel), {
        horizontal: "center",
      });
      setCellValue(sheet, "K4", `${sortedFiles.length}`, {
        horizontal: "center",
      });

      setCellValue(sheet, "C5", averageUltrasonic, { horizontal: "center" });
      setCellValue(sheet, "G5", averageTemperature, { horizontal: "center" });
      setCellValue(sheet, "K5", averageThreePhaseDiffValue, {
        horizontal: "center",
      });

      setCellValue(sheet, "C6", combinedOpinion, { horizontal: "left" });

      setCellValue(
        sheet,
        "A7",
        t("imageFileIndex.timeseries.chart.ultrasonicTrend", {
          defaultValue: "Ultrasonic Trend",
        }),
        { horizontal: "center" },
      );
      setCellValue(sheet, "G7", temperatureGraphTitle, {
        horizontal: "center",
      });

      addBase64Image(
        workbook,
        sheet,
        getChartBase64(ultrasonicChartRef),
        "A8:F9",
      );
      addBase64Image(
        workbook,
        sheet,
        getChartBase64(temperatureChartRef),
        "G8:L9",
      );

      // STEP 4: 대표 이미지별 날짜, 이미지, 측정값 비교 영역을 채운다.
      representativeBlocks.forEach((block, index) => {
        const representativeFile = visibleRepresentativeFiles[index];
        const dateAddress = getRangeStartAddress(block.date);
        const imageAddress = getRangeStartAddress(block.image);
        const fileNameAddress = getRangeStartAddress(
          block.fields.fileName.value,
        );
        const ultrasonicAddress = getRangeStartAddress(
          block.fields.ultrasonic.value,
        );
        const detectedTemperatureAddress = getRangeStartAddress(
          block.fields.detectedTemperature.value,
        );
        const threePhaseDifferenceAddress = getRangeStartAddress(
          block.fields.threePhaseDifference.value,
        );
        const inspectionResultAddress = getRangeStartAddress(
          block.fields.inspectionResult.value,
        );

        setCellValue(
          sheet,
          dateAddress,
          getDisplayText(
            representativeFile?.shooting_date ||
              representativeFile?.uploaded_at,
          ),
          { horizontal: "center" },
        );

        setCellValue(
          sheet,
          fileNameAddress,
          representativeFile
            ? getDisplayText(representativeFile?.file_name)
            : "-",
          { horizontal: "center" },
        );

        setCellValue(
          sheet,
          ultrasonicAddress,
          representativeFile
            ? `${formatMetricValue(representativeFile?.avg_decibel)} dB`
            : "-",
          { horizontal: "center" },
        );

        setCellValue(
          sheet,
          detectedTemperatureAddress,
          representativeFile
            ? `${formatMetricValue(representativeFile?.thermal_center)} \u00B0C`
            : "-",
          { horizontal: "center" },
        );

        setCellValue(
          sheet,
          threePhaseDifferenceAddress,
          representativeFile
            ? `${formatThreePhaseDifferenceValue(
                representativeFile?.click_points,
              )} \u00B0C`
            : "-",
          { horizontal: "center" },
        );

        setCellValue(
          sheet,
          inspectionResultAddress,
          representativeFile
            ? getFusionResultLabel(
                representativeFile?.fusion_result_code ??
                  representativeFile?.fusion_result,
                i18n,
                {
                  variant: "image",
                  fallback: getDisplayText(representativeFile?.fusion_result),
                },
              )
            : "-",
          { horizontal: "center" },
        );

        const imageSource = representativeFile
          ? getReportImageSource(
              fileImages?.[String(representativeFile?.upload_id ?? "")],
              representativeFile,
              imageType,
            )
          : "";

        if (imageSource) {
          addBase64Image(workbook, sheet, imageSource, block.image);
        } else {
          setCellValue(
            sheet,
            imageAddress,
            t("imageFileIndex.timeseries.detail.noImage", {
              defaultValue: "No Image",
            }),
            { horizontal: "center" },
          );
        }
      });

      // STEP 5: 완성된 워크북을 파일로 저장한다.
      sheet.pageSetup.printArea = "A1:L16";

      const fileName =
        file?.file_clean_file_name ||
        file?.clean_file_name ||
        file?.upload_id ||
        "timeseries_report";

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(blob, `${fileName}.xlsx`);
    } catch (error) {
      console.error("[TimeseriesExcelDownloadBtn] download failed", error);
      showAlert({
        text: downloadFailedMessage,
        success: false,
        autoClose: true,
      });
    }
  };

  return (
    <button
      className="btn-bg-blue"
      onClick={handleDownload}
      title={!canDownload ? representativeGuideMessage : undefined}
    >
      {t("imageFileIndex.buttons.ExcelDownload")}
    </button>
  );
}
